'use client'

/**
 * Header Losi mark
 *
 * The Losi character used as a brand logo in headers. On mount (and on click)
 * it plays a random intro pose, then one or two random ambient poses that never
 * repeat back-to-back, and always settles on the `logo` state. This keeps the
 * mark playful and non-repeating while guaranteeing it ends as the Losi logo.
 *
 * Reuses the same LosiAvatar engine + ambient pool as MessageLosiMark, but with
 * a self-terminating "play then rest on logo" lifecycle instead of being tied
 * to an assistant turn.
 */

import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react'

import { LosiAvatar } from '@/components/losi-bot/losi-avatar'
import { AMBIENT_LOADER_STATES } from '@/lib/losi-avatar/chat-status'
import { STATE_BY_ID, type StateId } from '@/lib/losi-engine/states'
import { cn } from '@/lib/utils'

const INTROS = ['idle', 'comet'] as const
const AMBIENT_PAD_MS = 350

function holdMsFor(state: StateId): number {
  const def = STATE_BY_ID.get(state)
  const seconds = Math.max(def?.minDuration ?? 0, def?.duration ?? 1.8)
  return Math.round(seconds * 1000) + AMBIENT_PAD_MS
}

function pickIntro(): StateId {
  return INTROS[Math.floor(Math.random() * INTROS.length)]!
}

function nextAmbient(avoid?: StateId | null): StateId {
  const pool = AMBIENT_LOADER_STATES.filter((id) => id !== avoid)
  const from = pool.length > 0 ? pool : [...AMBIENT_LOADER_STATES]
  return from[Math.floor(Math.random() * from.length)]!
}

export interface HeaderLosiMarkProps {
  size?: number
  className?: string
  shape?: string
  /** Optional hover animation — off unless you pass a state (e.g. `wobble`). */
  hoverState?: StateId | null
}

export function HeaderLosiMark({
  size = 28,
  className,
  shape,
  hoverState = null
}: HeaderLosiMarkProps) {
  const [state, setState] = useState<StateId>('logo')
  const timers = useRef<number[]>([])

  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }, [])

  // Play a random, non-repeating sequence that always ends on `logo`.
  const play = useCallback(() => {
    clearTimers()
    const intro = pickIntro()
    const first = nextAmbient(intro)
    const second = nextAmbient(first)

    setState(intro)
    let elapsed = holdMsFor(intro)

    timers.current.push(window.setTimeout(() => setState(first), elapsed))
    elapsed += holdMsFor(first)

    timers.current.push(window.setTimeout(() => setState(second), elapsed))
    elapsed += holdMsFor(second)

    // Always resolve back to the logo.
    timers.current.push(window.setTimeout(() => setState('logo'), elapsed))
  }, [clearTimers])

  useEffect(() => {
    play()
    return clearTimers
  }, [play, clearTimers])

  const onClick = useCallback(() => {
    play()
  }, [play])

  return (
    <LosiAvatar
      state={state}
      expression="neutre"
      size={size}
      shape={shape}
      fitBody
      hoverState={hoverState}
      className={cn(className)}
      onClick={onClick}
      title="Losi"
    />
  )
}
